import { CheckIcon, Cross2Icon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  CheckboxGroup,
  Flex,
  ScrollArea,
  Spinner,
  Text,
} from "@radix-ui/themes";
import { Control, Controller, FieldErrors } from "react-hook-form";
import {
  ComposedName,
  FieldShell,
  fieldAria,
  fieldIds,
  resolveFieldError,
} from "../FieldShell";

interface CheckboxGroupProps {
  name: string;
  label: string;
  control: Control<any>;
  errors: FieldErrors<any>;
  style?: any;
  options?: { value: string; label: string }[];
  itemsLoading?: boolean;
  itemsError?: boolean;
  handleItemClick?: any;
  handleItemChange?: any;
  composedName?: ComposedName;
  required?: boolean;
  hint?: string;
  disabled?: boolean;
}

const CheckboxGroupComponent = ({
  name,
  label,
  control,
  errors,
  style,
  options,
  itemsLoading,
  itemsError,
  handleItemClick,
  handleItemChange,
  composedName,
  required,
  hint,
  disabled,
}: CheckboxGroupProps) => {
  const error = resolveFieldError(errors, name, composedName);
  const { fieldId } = fieldIds(name);

  return (
    <Box {...style}>
      <FieldShell
        name={name}
        label={label}
        required={required}
        error={error}
        hint={hint}
        labelAs="div"
      >
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const selectedCount = Array.isArray(field.value) ? field.value.length : 0;
            const hasOptions = !!options?.length;

            return (
              <Flex direction="column" gap="2">
                {hasOptions && !disabled && (
                  <Flex gap="2" align="center" wrap="wrap">
                    <Button
                      type="button"
                      variant="soft"
                      size="1"
                      onClick={() => field.onChange(options!.map((option) => option.value))}
                    >
                      <CheckIcon /> Marcar todos
                    </Button>
                    <Button
                      type="button"
                      variant="soft"
                      color="gray"
                      size="1"
                      disabled={selectedCount === 0}
                      onClick={() => field.onChange([])}
                    >
                      <Cross2Icon /> Desmarcar todos
                    </Button>
                    {selectedCount > 0 && (
                      <Text size="1" color="gray">
                        {selectedCount} de {options!.length} seleccionado
                        {selectedCount > 1 ? "s" : ""}
                      </Text>
                    )}
                  </Flex>
                )}

                <CheckboxGroup.Root
                  onValueChange={(value) => {
                    field.onChange(value);
                    if (typeof handleItemChange === "function") {
                      handleItemChange(value);
                    }
                  }}
                  value={field.value || []}
                  disabled={disabled}
                  aria-labelledby={fieldId}
                  {...fieldAria(name, error, hint)}
                >
                  <ScrollArea
                    type="auto"
                    scrollbars="vertical"
                    style={{ maxHeight: "300px" }}
                  >
                    <Box
                      pr="3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: "var(--space-2)",
                      }}
                    >
                      {itemsLoading ? (
                        <Flex align="center" gap="2">
                          <Spinner size="1" />
                          <Text size="1" color="gray">
                            A carregar…
                          </Text>
                        </Flex>
                      ) : itemsError ? (
                        <Text size="1" color="red">
                          Erro ao carregar as opções.
                        </Text>
                      ) : hasOptions ? (
                        options!.map((option) => (
                          <CheckboxGroup.Item
                            key={option.value}
                            value={option.value}
                            onClick={() => {
                              if (typeof handleItemClick === "function") {
                                handleItemClick(option.value);
                              }
                            }}
                          >
                            {option.label}
                          </CheckboxGroup.Item>
                        ))
                      ) : (
                        <Text size="1" color="gray">
                          Nenhum item disponível
                        </Text>
                      )}
                    </Box>
                  </ScrollArea>
                </CheckboxGroup.Root>
              </Flex>
            );
          }}
        />
      </FieldShell>
    </Box>
  );
};

export default CheckboxGroupComponent;
